(() => {
  if (customElements.get('nw-restock-notice')) return;

  class RestockNotice extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      try { this.variants = JSON.parse(this.querySelector('[data-restock-variants]').textContent); }
      catch { this.hidden = true; return; }
      this.form = this.querySelector('[data-restock-form]');
      this.email = this.form?.querySelector('[name="email"]');
      this.button = this.form?.querySelector('button');
      this.nativeFormId = this.dataset.nativeFormId === 'U2Sqgs' && this.dataset.klaviyoSiteId === 'RgtfBR'
        ? 'U2Sqgs' : '';
      this.nativeLauncher = this.querySelector('[data-restock-native-launch]');
      this.nativeWrap = this.querySelector('[data-restock-native-wrap]');
      this.feedback = this.querySelector('[data-restock-feedback]');
      this.feedbackCopy = this.querySelector('[data-restock-feedback-copy]');
      this.continueLink = this.querySelector('[data-restock-continue]');
      this.saved = new Map();
      this.attempts = new Map();
      this.registered = new Set();
      this.owner = this.closest('.product__info-container');
      // Status sits beside the price; keep one custom element owning show/hide.
      this.owner?.addEventListener('change', (event) => {
        if (!event.target.closest('variant-radios, variant-selects')) return;
        this.request?.abort();
        this.request = null;
        this.busy = false;
        if (this.nativeOpenRequested || this.nativeFormOpen) this.closeNative();
        this.pending = true;
        this.message('');
        this.lock();
      }, { capture: true, signal });
      document.addEventListener('nw:pdp-variant-committed', (event) => {
        if (String(event.detail?.sectionId) === this.dataset.section) this.select(event.detail.variantId);
      }, { signal });
      this.owner?.addEventListener('click', (event) => {
        if (!event.target.closest('[data-nw-restock-trigger]')) return;
        event.preventDefault();
        this.scrollIntoView({ block: 'center', behavior: 'auto' });
        if (this.nativeFormId && !this.saved.has(this.dataset.variantId)) this.openNative(event.target.closest('[data-nw-restock-trigger]'));
        else (this.form?.hidden || this.nativeWrap?.hidden ? this.feedback : this.email)?.focus({ preventScroll: true });
      }, { signal });
      this.nativeLauncher?.addEventListener('click', () => this.openNative(), { signal });
      window.addEventListener('klaviyoForms', (event) => this.onNativeEvent(event), { signal });
      window.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || event.isComposing || !this.nativeFormOpen) return;
        if (this.closeNative()) { event.preventDefault(); event.stopPropagation(); }
      }, { signal, capture: true });
      this.form?.addEventListener('submit', (event) => this.submit(event), { signal });
      this.select(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      clearTimeout(this.nativeTimer);
      this.nativeObserver?.disconnect();
      this.controller = null;
    }

    get status() {
      // Variant rendering can replace the price/status host without replacing
      // this custom element. Resolve the current node when reconciling it.
      return this.owner?.querySelector('[data-restock-status-host] .restock-notice__status')
        || this.querySelector('.restock-notice__status');
    }

    select(id) {
      if (this.nativeVariantId && this.nativeVariantId !== String(id)) this.closeNative();
      this.request?.abort();
      this.request = null;
      this.busy = false;
      this.pending = false;
      this.variant = this.variants.find((variant) => String(variant.id) === String(id));
      this.hidden = !this.variant || this.variant.available;
      if (this.status) this.status.hidden = this.hidden;
      this.dataset.variantId = String(id);
      if (!this.variant) return;
      const title = this.variant.title === 'Default Title' ? 'this product' : this.variant.title;
      const option = this.status?.querySelector('[data-restock-option]')
        || this.querySelector('[data-restock-option]');
      if (option) option.hidden = true;
      const copy = this.querySelector('[data-restock-copy]');
      if (copy) copy.textContent = `Get notified when ${title} is back in stock.`;
      if (this.form) this.form.hidden = this.saved.has(String(id));
      if (this.nativeWrap) this.nativeWrap.hidden = this.saved.has(String(id));
      if (this.nativeLauncher) this.nativeLauncher.setAttribute('aria-label', `Request a restock email for ${title}`);
      this.message(this.saved.has(String(id)) ? this.confirmation(title, this.saved.get(String(id))) : '');
      this.updateSwatches();
      this.lock();
    }

    updateSwatches() {
      this.owner?.querySelectorAll('variant-radios fieldset').forEach((fieldset, position) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const matches = this.variants.filter((variant) => variant.options.every((option, index) =>
            option === (index === position ? input.value : this.variant.options[index])));
          const available = matches.some((variant) => variant.available);
          input.dataset.optionAvailable = String(available);
          input.setAttribute('aria-label', `${input.name}: ${input.value}${available ? '' : matches.length ? ', sold out' : ', unavailable combination'}`);
        });
      });
    }

    lock() {
      if (this.nativeLauncher) this.nativeLauncher.disabled = this.hidden || this.pending || this.busy
        || this.nativeOpenRequested || this.nativeFormOpen || !this.nativeFormId;
      const disabled = this.hidden || this.pending || this.busy || !this.endpoint();
      if (this.email) this.email.disabled = disabled;
      if (this.button) {
        this.button.disabled = disabled;
        const label = this.button.querySelector('[data-restock-submit-label]') || this.button;
        label.textContent = this.busy ? 'SAVING…' : 'NOTIFY ME';
      }
      if (this.form) this.form.setAttribute('aria-busy', String(Boolean(this.busy || this.pending)));
    }

    openNative(returnFocus = this.nativeLauncher) {
      if (!this.nativeFormId || this.hidden || this.pending || this.nativeOpenRequested || this.nativeFormOpen
        || this.saved.has(this.dataset.variantId) || this.variant?.available) return false;
      // Capture owns the native report. A requested open is never a form view.
      this.nativeVariantId = this.dataset.variantId;
      this.nativeOpenRequested = true;
      this.nativeReturnFocus = returnFocus;
      this.dataset.nativeState = 'opening';
      this.message('');
      this.lock();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', this.nativeFormId]);
      clearTimeout(this.nativeTimer);
      this.nativeTimer = setTimeout(() => {
        if (this.nativeFormOpen) return;
        this.nativeOpenRequested = false;
        this.dataset.nativeState = 'unavailable';
        this.message('The alert form is taking longer to load. Please try again.', true);
        this.lock();
      }, 6000);
      return true;
    }

    closeNative() {
      const form = document.querySelector(`[data-testid="klaviyo-form-${this.nativeFormId}"]`);
      const close = form?.closest('[role="dialog"]')?.querySelector('button.klaviyo-close-form');
      if (!close) return false;
      close.click();
      return true;
    }

    onNativeEvent(event) {
      const detail = event.detail || {};
      if (!this.nativeFormId || String(detail.formId || detail.formID || '') !== this.nativeFormId
        || !this.nativeVariantId) return;
      this.dataset.nativeEvent = String(detail.type || '');
      if (detail.type === 'open') {
        clearTimeout(this.nativeTimer);
        this.nativeOpenRequested = false;
        this.nativeFormOpen = true;
        if (this.nativeVariantId !== this.dataset.variantId || this.hidden || this.pending) {
          this.closeNative();
          return;
        }
        this.dataset.nativeState = 'open';
        this.lock();
        const focusEmail = () => {
          const form = document.querySelector(`[data-testid="klaviyo-form-${this.nativeFormId}"]`);
          const input = form?.querySelector('input[type="email"]');
          if (!input) return false;
          // The provider installs its focus trap after the open callback.
          // Focus the chosen input after paint, once that trap is ready.
          const focus = () => {
            if (this.nativeFormOpen && input.isConnected) input.focus({ preventScroll: true });
          };
          this.afterNativePaint(focus);
          return true;
        };
        this.nativeObserver?.disconnect();
        if (!focusEmail()) {
          this.nativeObserver = new MutationObserver(() => {
            if (focusEmail()) this.nativeObserver.disconnect();
          });
          this.nativeObserver.observe(document.body, { childList: true, subtree: true });
        }
      } else if (detail.type === 'submitBackInStockForm' && this.nativeFormOpen
        && this.nativeVariantId === this.dataset.variantId && !this.variant?.available) {
        // The native BIS form emits its own acknowledgement, rather than the
        // newsletter form's generic submit callback. This is notification
        // consent only. A step callback is not a completed request.
        // Native submit confirms this notification request, never marketing
        // consent. Do not duplicate it through the legacy subscription API.
        const title = this.variant.title === 'Default Title' ? 'this product' : this.variant.title;
        this.saved.set(this.nativeVariantId, 'ready');
        if (this.nativeWrap) this.nativeWrap.hidden = true;
        this.dataset.nativeState = 'submitted';
        this.message(this.confirmation(title, 'ready'));
      } else if (detail.type === 'close') {
        clearTimeout(this.nativeTimer);
        this.nativeObserver?.disconnect();
        this.nativeOpenRequested = false;
        this.nativeFormOpen = false;
        this.nativeVariantId = '';
        this.dataset.nativeState = 'closed';
        this.lock();
        const target = this.nativeWrap?.hidden ? this.feedback : this.nativeReturnFocus;
        this.afterNativePaint(() => {
          if (this.nativeFormOpen) return;
          if (target?.isConnected && !target.closest('[hidden]')) target.focus({ preventScroll: true });
          else if (!this.hidden) this.nativeLauncher?.focus({ preventScroll: true });
        });
      }
    }

    afterNativePaint(callback) {
      if (window.requestAnimationFrame) window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
      else callback();
    }

    endpoint() {
      const value = this.dataset.endpoint || '';
      // The theme accepts only a same-store Shopify app proxy. No email in URLs.
      return /^\/apps\/[a-z0-9-]+(?:\/[a-z0-9-]+)*$/.test(value) ? value : null;
    }

    message(text, error = false) {
      if (!this.feedback) return;
      const copy = this.feedbackCopy || this.feedback;
      this.feedback.hidden = !text;
      this.feedback.dataset.error = String(error);
      this.feedback.setAttribute('role', error ? 'alert' : 'status');
      copy.textContent = text;
      if (this.continueLink) this.continueLink.hidden = !text || error || !/on the list/.test(text);
    }

    confirmation(title, status) {
      return status === 'ready' ? `You're on the list for ${title}. We'll email you when it's back.`
        : status === 'unconfirmed' ? `Your request was saved for ${title}, but we couldn't register the alert. Please contact us for help.`
        : `Your request is saved for ${title}. We're still registering your alert.`;
    }

    async checkStatus(result, request, variantId, title) {
      if (result.status !== 'saved' || !/^[a-f0-9-]{36}$/i.test(result.receipt || '')) return result;
      const endpoint = this.endpoint()?.replace(/\/subscribe$/, '/status');
      if (!endpoint || endpoint === this.endpoint()) return result;
      for (let attempt = 0; attempt < 6; attempt++) {
        if (request.signal.aborted || this.request !== request) return;
        await new Promise((resolve) => {
          const done = () => { clearTimeout(timer); request.signal.removeEventListener('abort', done); resolve(); };
          const timer = setTimeout(done, 1500);
          request.signal.addEventListener('abort', done, { once: true });
        });
        if (request.signal.aborted || this.request !== request) return;
        const response = await fetch(`${endpoint}?receipt=${encodeURIComponent(result.receipt)}`, {
          credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' },
        });
        const status = await response.json();
        if (this.request !== request || this.dataset.variantId !== variantId) return;
        if (!response.ok || status.accepted !== true || String(status.variantId) !== variantId
          || status.receipt !== result.receipt || status.enrollment !== result.enrollment
          || !['saved', 'ready', 'linked', 'unconfirmed'].includes(status.status)) return result;
        if (result.enrollment !== 'client') {
          this.saved.set(variantId, status.status);
          this.message(this.confirmation(title, status.status), status.status === 'unconfirmed');
        }
        if (status.status !== 'saved') return status;
      }
      return result;
    }

    async registerNative(email, variantId, identity, request) {
      if (this.registered.has(identity)) return;
      // This public site ID belongs to Nordweg US. No private key or marketing
      // subscription is involved. Klaviyo owns suppression, waiting and sending.
      if (this.dataset.klaviyoSiteId !== 'RgtfBR') throw new Error('UNCONFIRMED');
      const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=RgtfBR', {
        method: 'POST', credentials: 'omit', signal: request.signal, redirect: 'error', referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({ data: { type: 'back-in-stock-subscription', attributes: {
          profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'],
        }, relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } } } }),
      });
      if (this.feedback) this.feedback.dataset.transport = `klaviyo:${response.status}`;
      if (response.status !== 202) throw new Error('UNCONFIRMED');
      this.registered.add(identity);
    }

    async submit(event) {
      event.preventDefault();
      if (this.hidden || this.pending || this.busy || !this.endpoint() || !this.form.reportValidity()) return;
      const variantId = String(this.variant.id);
      const title = this.variant.title === 'Default Title' ? 'this product' : this.variant.title;
      const email = this.email.value.trim();
      const identity = `${variantId}:${email.toLowerCase()}`;
      if (!this.attempts.has(identity)) this.attempts.set(identity, crypto.randomUUID());
      const requestKey = this.attempts.get(identity);
      const request = new AbortController();
      this.request = request;
      this.busy = true;
      this.message('');
      this.lock();
      const timeout = setTimeout(() => request.abort(), 20000);
      try {
        const response = await fetch(this.endpoint(), {
          method: 'POST', credentials: 'same-origin', signal: request.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email, productHandle: this.dataset.productHandle,
            productId: this.dataset.productId, variantId, consent: 'restock_email', source: 'pdp_restock', requestKey }),
        });
        if (this.feedback) this.feedback.dataset.transport = `shopify:${response.status}`;
        let result = await response.json();
        if (this.feedback) this.feedback.dataset.responseCode = ['INVALID', 'UNAUTHORIZED', 'UNAVAILABLE', 'AVAILABLE',
          'INVALID_VARIANT', 'RATE_LIMITED', 'CATALOG_UNAVAILABLE', 'SAVE_FAILED'].includes(result.reason) ? result.reason : '';
        if (this.request !== request || this.dataset.variantId !== variantId) return;
        if (!response.ok || result.accepted !== true || String(result.variantId) !== variantId
          || !['saved', 'ready', 'linked', 'unconfirmed'].includes(result.status)
          || (result.status === 'linked' && result.enrollment !== 'client')) {
          throw new Error(result.reason === 'AVAILABLE' ? 'AVAILABLE' : 'UNCONFIRMED');
        }
        if (result.enrollment === 'client') {
          this.message(`Saving your request for ${title}…`);
          result = await this.checkStatus(result, request, variantId, title);
          if (request.signal.aborted || this.request !== request || this.dataset.variantId !== variantId) return;
          if (result?.status !== 'linked') throw new Error(result?.status === 'saved' ? 'LINK_PENDING' : 'UNCONFIRMED');
          await this.registerNative(email, variantId, identity, request);
          if (this.request !== request || this.dataset.variantId !== variantId) return;
          result = { ...result, status: 'ready' };
        }
        this.saved.set(variantId, result.status);
        this.attempts.delete(identity);
        this.email.value = '';
        this.form.hidden = true;
        this.message(this.confirmation(title, result.status), result.status === 'unconfirmed');
        this.feedback.focus({ preventScroll: true });
        // Status checks carry only an opaque receipt. A polling failure cannot
        // undo durable capture or turn a saved request into a false failure.
        await this.checkStatus(result, request, variantId, title).catch(() => {});
      } catch (error) {
        if (this.request !== request) return;
        this.message(error.message === 'AVAILABLE'
          ? 'This selection is available again. Refresh the page to shop it.'
          : error.message === 'LINK_PENDING' ? 'Your request is saved. Please try again to finish registering the alert.'
            : 'We could not confirm your request. Please try again.', true);
      } finally {
        clearTimeout(timeout);
        if (this.request === request) { this.request = null; this.busy = false; this.lock(); }
      }
    }
  }

  customElements.define('nw-restock-notice', RestockNotice);
})();
